import type {
  BetError,
  BetSlipErrorResponse,
  BetSlipItem,
  BetSlipResponse,
  MultiInvestment,
} from '@mobi/betslip/types'
import { mapResponse } from '@mobi/betslip/Store/Bets/helpers/apiResponse/mapItemsWithResponse'
import { roundDownDecimal } from '@mobi/betslip/helpers/calculator/misc'
import { calculateMultiReturn } from '@mobi/betslip/helpers/calculator/multis'
import { BetErrorType, MultiErrorMapping, toBetErrorType } from '@core/Data/betting'
import {
  ProposeBet,
  ConfirmBet,
  SetBonusBet,
  ToggleBonusBetUsage,
  ProposeMultiBetSucceeded,
  ProposeMultiBetFailed,
  ConfirmMultiBetSucceeded,
  ConfirmMultiBetFailed,
  MultiBetResponsePriceChange,
} from '@core/Areas/Quickbet/signals'
import { UpdateAccountBalance } from '@core/State/UserAccount/userAccountDriver'
import { fetchCampaignsAsync } from '@core/State/UserAccount/async-signals'
import { postAllBetsToServer } from '@core/Areas/BetSlip/helpers/postAllBetsToServer'
import { trackOptimoveEvent } from '@core/Services/Optimove/optimove'
import { shouldShowPriceChangeMessage } from '@core/Areas/Quickbet/helpers/state/shouldShowPriceChangeMessage'
import { QuickbetState, state$ as quickbetState$ } from '@core/Areas/Quickbet/driver'

export async function proposeMultiBetAsync({ items, investment }: MultiBetParams): Promise<void> {
  ProposeBet()
  try {
    const response = await postAllBetsToServer('propose', {
      items,
      multiInvestment: { ...investment, f1: 0, f2: 0, f3: 0, f4: 0, f5: 0 },
      multiBetError: null,
    })
    quickbetState$.take(1).subscribe(quickbetStateRecord => {
      const state = quickbetStateRecord.toJS() as QuickbetState
      const currentMultiItems = state.selectionMultiLegs
      const newMultiItems = state.selectionMultiLegs?.map(item =>
        mapResponse(item, response, response[0])
      )

      if (!currentMultiItems || !newMultiItems) throw new Error('No Legs Selected for Multi')

      if (response[0].success) {
        if (shouldShowMultiPriceChange(currentMultiItems, newMultiItems)) {
          MultiBetResponsePriceChange(newMultiItems)
          return
        }

        ProposeMultiBetSucceeded(newMultiItems)
        return
      }

      const multiBetError = getMultiBetErrorDescription(response[0])
      const errorResponse = multiBetError
        ? {
            response: { message: multiBetError.errorMessage },
            code: multiBetError.betErrorType,
          }
        : undefined
      ProposeMultiBetFailed(errorResponse)
    })
  } catch (error) {
    ProposeMultiBetFailed(error as BetSlipErrorResponse)
  }
}

export async function confirmMultiBetAsync({
  items,
  investment,
  copiedBetLoopPostId,
}: MultiBetParams): Promise<void> {
  ConfirmBet()
  try {
    const response = await postAllBetsToServer(
      'confirm',
      {
        items,
        multiInvestment: { ...investment, f1: 0, f2: 0, f3: 0, f4: 0, f5: 0 },
        multiBetError: null,
      },
      { multiBetLoopPostId: copiedBetLoopPostId }
    )
    quickbetState$.take(1).subscribe(quickbetStateRecord => {
      const state = quickbetStateRecord.toJS() as QuickbetState
      const currentMultiItems = state.selectionMultiLegs
      const newMultiItems = state.selectionMultiLegs?.map(item =>
        mapResponse(item, response, response[0])
      )

      if (!currentMultiItems || !newMultiItems) throw new Error('No Legs Selected for Multi')

      if (response[0].success) {
        ConfirmMultiBetSucceeded(response)

        if (response[0].receipt?.campaignActivatedInd) {
          fetchCampaignsAsync()
          trackOptimoveEvent({ eventName: 'redeemed_bonus_offer' })
        }

        if (response[0].receipt?.accountBalance) {
          UpdateAccountBalance({ balance: response[0].receipt.accountBalance })
        }

        SetBonusBet(null)
        ToggleBonusBetUsage(false)
        return
      }

      if (shouldShowMultiPriceChange(currentMultiItems, newMultiItems)) {
        MultiBetResponsePriceChange(newMultiItems)
        return
      }

      const multiBetError = getMultiBetErrorDescription(response[0])
      const errorResponse: BetSlipErrorResponse | undefined = multiBetError
        ? {
            response: { message: multiBetError.errorMessage },
            code: multiBetError.betErrorType,
          }
        : undefined
      ConfirmMultiBetFailed(errorResponse)
    })
  } catch (error) {
    ConfirmMultiBetFailed(error as BetSlipErrorResponse)
  }
}

// =============
// Local Helpers
// =============

const getBetErrorType = (
  response: { error: BetError | null } | undefined
): BetErrorType | undefined => {
  if (!response || !response.error) return undefined
  return toBetErrorType(response.error.type ?? BetErrorType.Unspecified)
}

function shouldShowMultiPriceChange(currentItems: BetSlipItem[], newItems: BetSlipItem[]) {
  const currentPrice = roundDownDecimal(calculateMultiReturn(currentItems, { shouldRound: true }))
  const newPrice = roundDownDecimal(calculateMultiReturn(newItems, { shouldRound: true }))
  return shouldShowPriceChangeMessage({ winPrice: currentPrice }, { winPrice: newPrice })
}

const getMultiBetErrorDescription = (
  multiBetResponse: BetSlipResponse | undefined
): MultiBetError | null => {
  if (!!multiBetResponse && !multiBetResponse.success) {
    const betErrorType = getBetErrorType(multiBetResponse) || BetErrorType.Unspecified
    return {
      betErrorType,
      errorMessage:
        MultiErrorMapping.get(BetErrorType[betErrorType]) ||
        multiBetResponse.error?.message ||
        'An error has occurred',
    }
  } else {
    return null
  }
}

// =====
// Types
// =====

type MultiBetParams = {
  items: BetSlipItem[]
  investment: Pick<MultiInvestment, 'value' | 'bonusBetId' | 'isBonusBet' | 'isBonusCash'>
  copiedBetLoopPostId?: string
}
interface MultiBetError {
  betErrorType: BetErrorType
  errorMessage: string
}
